import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Quote, SupplierRate } from '../../types/quote.types';

interface PricingConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  quote: Quote | null;
  selectedSupplier: SupplierRate | null;
  onConfirm: (quote: Quote, selectedSupplier: SupplierRate) => void;
  loading?: boolean;
}

const PricingConfirmationModal: React.FC<PricingConfirmationModalProps> = ({
  open,
  onClose,
  quote,
  selectedSupplier,
  onConfirm,
  loading = false,
}) => {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!quote || !selectedSupplier) return;
    
    setConfirming(true);
    try {
      await onConfirm(quote, selectedSupplier);
    } finally {
      setConfirming(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount == null || !Number.isFinite(amount)) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  // Audit 13/F-156: null = no meaningful baseline → 'N/A', not a 0% saving
  const formatPercentage = (percentage: number | null | undefined) => {
    if (percentage == null || !Number.isFinite(percentage)) return 'N/A';
    const sign = percentage > 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  const getSavingsColor = (percentage: number | null | undefined) => {
    if (percentage == null || !Number.isFinite(percentage)) return 'default';
    return percentage > 0 ? 'success' : 'error';
  };

  if (!quote || !selectedSupplier) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Pricing Confirmation
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Quote Ref: {quote.quoteRef}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Client & Property Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client & Property Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Client
                    </Typography>
                    <Typography variant="body1">
                      {quote.client?.companyName || `${quote.client?.firstName} ${quote.client?.lastName}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Property
                    </Typography>
                    <Typography variant="body1">
                      {quote.property?.address || 'No property specified'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Meter Type
                    </Typography>
                    <Typography variant="body1">
                      {quote.meterType === 'MPAN' ? 'Electricity' : 'Gas'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {quote.meterType === 'MPAN' ? 'EAC' : 'AQ'}
                    </Typography>
                    <Typography variant="body1">
                      {quote.meterType === 'MPAN' ? quote.eac : quote.aq} kWh
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Selected Supplier Details */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Selected Supplier: {selectedSupplier.supplierName}
                  </Typography>
                  <Chip
                    label={formatPercentage(selectedSupplier.percentageDifference)}
                    color={getSavingsColor(selectedSupplier.percentageDifference)}
                    size="medium"
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Standing Charge
                    </Typography>
                    <Typography variant="body1">
                      {selectedSupplier.standingCharge ? `${selectedSupplier.standingCharge}p/day` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Day Unit Rate
                    </Typography>
                    <Typography variant="body1">
                      {selectedSupplier.dayUnitRate ? `${selectedSupplier.dayUnitRate}p/kWh` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Contract Period
                    </Typography>
                    <Typography variant="body1">
                      {selectedSupplier.contractPeriodMonths} months
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Annual Cost
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedSupplier.annualExpenditure)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Cost Comparison */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cost Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Annual Cost</TableCell>
                        <TableCell align="right">Monthly Cost</TableCell>
                        <TableCell align="right">Savings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            Current: {quote.currentSupplier.supplierName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(quote.currentSupplier.annualExpenditure)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(quote.currentSupplier.monthlyExpenditure)}
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: 'primary.light', '& td': { fontWeight: 'bold' } }}>
                        <TableCell>
                          <Typography variant="body1">
                            Selected: {selectedSupplier.supplierName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(selectedSupplier.annualExpenditure)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(selectedSupplier.monthlyExpenditure)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={formatPercentage(selectedSupplier.percentageDifference)}
                            color={getSavingsColor(selectedSupplier.percentageDifference)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Annual Savings Summary */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ backgroundColor: 'success.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Annual Savings Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Annual Savings
                    </Typography>
                    <Typography variant="h5" color="success.dark">
                      {quote.currentSupplier.annualExpenditure != null && selectedSupplier.annualExpenditure != null
                        ? formatCurrency(
                            quote.currentSupplier.annualExpenditure - selectedSupplier.annualExpenditure
                          )
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Monthly Savings
                    </Typography>
                    <Typography variant="h5" color="success.dark">
                      {quote.currentSupplier.monthlyExpenditure != null && selectedSupplier.monthlyExpenditure != null
                        ? formatCurrency(
                            quote.currentSupplier.monthlyExpenditure - selectedSupplier.monthlyExpenditure
                          )
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Percentage Savings
                    </Typography>
                    <Typography variant="h5" color="success.dark">
                      {formatPercentage(selectedSupplier.percentageDifference)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirming}
          startIcon={confirming ? <CircularProgress size={20} /> : null}
        >
          {confirming ? 'Selecting Quote...' : 'Confirm Selection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingConfirmationModal;
